import { useParams, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { films } from '@/mocks/films';
import type { Film } from '@/mocks/films';

const categoryMap: Record<string, string> = {
  drama: '电视剧',
  movie: '电影',
  variety: '综艺',
  anime: '动漫',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const film = useMemo(() => films.find((f: Film) => f.id === id), [id]);
  const relatedFilms = useMemo(() => {
    if (!film) return [];
    return films.filter((f: Film) => f.category === film.category && f.id !== film.id).slice(0, 6);
  }, [film]);

  if (!film) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-6">
          <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
        </div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">内容未找到</h1>
        <p className="text-foreground-500 mb-6">抱歉，您要查看的影视内容不存在或已下架。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </div>
          返回首页
        </Link>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryMap[film.category] || film.category, href: `/category/${film.category}` },
    { label: film.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${film.title} - ${film.englishTitle} | 木瓜影视`,
    description: film.description,
    author: {
      '@type': 'Organization',
      name: '木瓜影视',
    },
    publisher: {
      '@type': 'Organization',
      name: '木瓜影视',
    },
    datePublished: film.releaseDate,
    image: film.coverImage,
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Movie',
      name: film.title,
      director: {
        '@type': 'Person',
        name: film.director,
      },
      actor: film.actors.map((a: string) => ({
        '@type': 'Person',
        name: a,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${film.title} - 木瓜影视 | 高清在线观看 ${film.englishTitle}`}
        description={`在线观看${film.title}（${film.englishTitle}），${film.description.slice(0, 100)}。木瓜影视提供${film.title}高清在线播放，即点即播无需下载。`}
        keywords={`${film.title},${film.englishTitle},在线观看,木瓜影视,${film.subcategory},${film.region},${film.director},高清播放`}
        canonicalPath={`/article/${film.id}`}
        ogTitle={`${film.title} - 木瓜影视高清在线观看`}
        ogDescription={film.description.slice(0, 120)}
        ogType="article"
        schema={articleSchema}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <article className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="flex flex-col md:flex-row gap-8 md:gap-10">
          <div className="w-full md:w-[340px] shrink-0">
            <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100 sticky top-24">
              <img
                src={film.coverImage}
                alt={`${film.title} - ${film.englishTitle}`}
                title={`${film.title} ${film.subcategory} 在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
              {film.title}
            </h1>
            <p className="text-foreground-500 text-sm mb-5">{film.englishTitle}</p>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="bg-accent-100 text-accent-800 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                {film.rating} 分
              </span>
              <span className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                {categoryMap[film.category]}
              </span>
              <span className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                {film.subcategory}
              </span>
              <span className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                {film.region}
              </span>
              <span className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                {film.year}
              </span>
              <span className="bg-primary-100/60 text-primary-700 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                {film.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-8">
              <div className="bg-background-100 rounded-lg px-4 py-3">
                <span className="text-foreground-400 text-xs block mb-0.5">导演</span>
                <span className="text-foreground-800 text-sm font-medium">{film.director}</span>
              </div>
              <div className="bg-background-100 rounded-lg px-4 py-3">
                <span className="text-foreground-400 text-xs block mb-0.5">集数/片长</span>
                <span className="text-foreground-800 text-sm font-medium">{film.episodes}</span>
              </div>
              <div className="bg-background-100 rounded-lg px-4 py-3">
                <span className="text-foreground-400 text-xs block mb-0.5">上映/播出日期</span>
                <span className="text-foreground-800 text-sm font-medium">{film.releaseDate}</span>
              </div>
              <div className="bg-background-100 rounded-lg px-4 py-3">
                <span className="text-foreground-400 text-xs block mb-0.5">地区</span>
                <span className="text-foreground-800 text-sm font-medium">{film.region}</span>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">主演</h2>
              <div className="flex flex-wrap gap-2">
                {film.actors.map((actor: string) => (
                  <span
                    key={actor}
                    className="bg-background-100 text-foreground-700 text-sm px-4 py-1.5 rounded-full whitespace-nowrap"
                  >
                    {actor}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">标签</h2>
              <div className="flex flex-wrap gap-2">
                {film.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="bg-accent-100/60 text-accent-700 text-xs px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3">剧情简介</h2>
              <div className="bg-background-100 rounded-lg p-5">
                <p className="text-foreground-700 text-sm leading-relaxed">
                  {film.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to={`/category/${film.category}`}
                className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-5 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                返回{categoryMap[film.category]}列表
              </Link>
            </div>
          </div>
        </div>

        {relatedFilms.length > 0 && (
          <div className="mt-14 pt-10 border-t border-background-200">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedFilms.map((f: Film) => (
                <MovieCard key={f.id} film={f} />
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}